// Formatting helpers shared by the Fleet pages, the homepage Fleet widget and
// the branch kiosk's check-in history. Dates arrive from the API as ISO
// strings in UTC and are shown in the viewer's local time.

export const HISTORY_DAYS = 90;

// Chip tints — literal strings so Tailwind keeps them. Translucent so they
// read on solid and glass cards alike.
export const TONE_CHIP = {
  emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  amber: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
  rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  violet: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

// Solid swatches for the activity calendar squares and map pins.
export const TONE_SWATCH = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  slate: "bg-slate-400",
  violet: "bg-violet-500",
};

export const RESULT_META = {
  pass: { label: "Passed", tone: "emerald" },
  attention: { label: "Needs attention", tone: "amber" },
  unsafe: { label: "Not safe", tone: "rose" },
  out: { label: "Not in service", tone: "slate" },
  // Automatic (GPS odometer): no inspection that day, but the truck moved.
  missed: { label: "Drove, no inspection", tone: "rose" },
  // Set by a person, with a reason — see SERVICE_REASONS. Also the
  // odometer job's call for a day the truck was marked out of service.
  oos: { label: "Out of service", tone: "violet" },
  back: { label: "Back in service", tone: "emerald" },
};

// Worst-first, for summarizing several inspections on one day.
export const RESULT_RANK = { unsafe: 5, missed: 4, oos: 3, attention: 2, out: 1, pass: 0, back: 0 };

// Why a unit is out of service. Keys match server/routes/forklifts/
// fleetData.js SERVICE_REASONS (the server validates them).
export const SERVICE_REASONS = [
  { value: "maintenance", label: "Scheduled maintenance" },
  { value: "repair", label: "Repair / breakdown" },
  { value: "parts", label: "Waiting on parts" },
  { value: "damage", label: "Accident / damage" },
  { value: "inspection", label: "Failed inspection" },
  { value: "other", label: "Other" },
];

export const isFlagged = (entry) =>
  entry?.result === "attention" || entry?.result === "unsafe";

export const SOURCE_LABEL = {
  mobile: "Mobile app",
  kiosk: "Branch kiosk",
  gps: "GPS (automatic)",
  status: "Status change",
};

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date) ? null : date;
};

/** "Oct 6, 7:24 AM" (adds the year when it isn't this year). */
export const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return "—";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
};

/** "Oct 6, 2026" */
export const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Local midnight `daysAgo` days back (0 = today). */
export const startOfLocalDay = (daysAgo = 0) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysAgo);
  return date;
};

/** "YYYY-MM-DD" for the viewer's local calendar day. */
export const localDayKey = (value) => {
  const date = toDate(value);
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Whole local calendar days between `value` and today (0 = today). */
export const daysAgo = (value) => {
  const date = toDate(value);
  if (!date) return null;
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return Math.round((startOfLocalDay(0) - day) / 86400000);
};

/** "Today" / "Yesterday" / "5 days ago" / "Never". */
export const relativeDay = (value, never = "Never") => {
  const days = daysAgo(value);
  if (days === null) return never;
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

/**
 * Freshness of a forklift's last check-in. Forklifts are checked daily, so
 * today/yesterday reads green, up to a week amber, anything older red.
 */
export const checkinTone = (value) => {
  const days = daysAgo(value);
  if (days === null) return "rose";
  if (days <= 1) return "emerald";
  if (days <= 7) return "amber";
  return "rose";
};

/** Minutes -> "12 min" / "3 hr" / "2 days". */
export const formatAge = (minutes) => {
  if (minutes === null || minutes === undefined || !isFinite(minutes)) return "";
  if (minutes < 60) return `${Math.max(0, Math.round(minutes))} min`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} hr`;
  return `${Math.round(minutes / 1440)} days`;
};

/** 24848 / "24848" -> "24,848 mi". */
export const formatMiles = (value) => {
  const miles = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  if (!value || !isFinite(miles) || miles <= 0) return "—";
  return `${Math.round(miles).toLocaleString()} mi`;
};

// Matches QUICK_SECONDS in server/routes/forklifts/fleetData.js: a full
// checklist in under this almost certainly wasn't walked.
export const QUICK_SECONDS = 30;
export const isQuick = (entry) =>
  entry?.durationSeconds !== null && entry?.durationSeconds !== undefined && entry.durationSeconds < QUICK_SECONDS;

/** Median time and quick count for a set of inspections (untimed skipped). */
export const durationSummary = (entries) => {
  const times = entries
    .map((e) => e.durationSeconds)
    .filter((t) => t !== null && t !== undefined)
    .sort((a, b) => a - b);
  return {
    median: times.length ? times[Math.floor((times.length - 1) / 2)] : null,
    quick: times.filter((t) => t < QUICK_SECONDS).length,
    timed: times.length,
  };
};

/** 125 -> "2m 5s". */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins ? `${mins}m ${secs}s` : `${secs}s`;
};

/** "2025 Freightliner M2" from a decoded VIN, or "". */
export const vehicleModel = (info) =>
  info ? [info.year, info.make, info.model].filter(Boolean).join(" ") : "";

/**
 * Live GPS state for a chip: driving, idling (ignition on, stopped),
 * parked, or stale when the tracker hasn't reported in a day.
 */
export const gpsState = (gps) => {
  if (!gps) return { label: "No GPS", tone: "slate", key: "none" };
  if (gps.ageMinutes !== null && gps.ageMinutes > 24 * 60) {
    return { label: `No signal · ${formatAge(gps.ageMinutes)}`, tone: "slate", key: "stale" };
  }
  if (gps.ignition === "on" && gps.speed > 0) {
    return { label: `Driving · ${Math.round(gps.speed)} mph`, tone: "sky", key: "driving" };
  }
  if (gps.ignition === "on") return { label: "Idling", tone: "amber", key: "idling" };
  return { label: "Parked", tone: "emerald", key: "parked" };
};

export const gpsPlace = (gps) =>
  gps ? gps.landmark || gps.address || "" : "";

export const mapsUrl = (lat, lng) =>
  lat === null || lng === null || lat === undefined || lng === undefined
    ? ""
    : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

/** Natural sort for unit numbers ("2" before "10"). */
export const compareUnits = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });

/** "2026-10-06 07:24" in local time — sorts and parses cleanly in Excel. */
export const csvDateTime = (value) => {
  const date = toDate(value);
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${localDayKey(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * CSV columns for inspection rows. When the rows carry checklists (detail
 * fetches), every checklist item gets its own column, in first-seen order —
 * forklift kiosk, forklift mobile and truck checklists each add theirs.
 */
export const inspectionCsvColumns = (entries) => {
  const labels = [];
  entries.forEach((entry) =>
    (entry.checklist || []).forEach((item) => {
      if (!labels.includes(item.label)) labels.push(item.label);
    })
  );
  return [
    { label: "Date", value: (e) => csvDateTime(e.at) },
    { label: "Type", value: (e) => (e.type === "vehicle" ? "Vehicle" : "Forklift") },
    { label: "Unit", value: (e) => e.asset },
    // A renamed truck's entries show its current name; keep what was recorded.
    ...(entries.some((e) => e.recordedAsset)
      ? [{ label: "Recorded as", value: (e) => e.recordedAsset || e.asset }]
      : []),
    { label: "Branch", value: (e) => e.branch },
    { label: "Shift", value: (e) => e.shiftName || "" },
    { label: "Inspector", value: (e) => e.inspector },
    { label: "Result", value: (e) => RESULT_META[e.result]?.label || e.result },
    {
      label: "Flagged items",
      value: (e) => (e.flagged || []).map((f) => (f.note ? `${f.label}: ${f.note}` : f.label)).join("; "),
    },
    { label: "Notes", value: (e) => e.notes },
    { label: "Odometer", value: (e) => e.odometer || "" },
    { label: "Time taken (s)", value: (e) => (e.durationSeconds ?? "") },
    { label: "Source", value: (e) => SOURCE_LABEL[e.source] || e.source },
    { label: "Entry #", value: (e) => e.entryID },
    ...labels.map((label) => ({
      label,
      value: (e) => {
        const item = (e.checklist || []).find((i) => i.label === label);
        if (!item) return "";
        return item.note ? `${item.value}: ${item.note}` : item.value;
      },
    })),
  ];
};

/** Rows -> CSV text. columns: [{ label, value: (row) => any }]. */
export const toCsv = (rows, columns) => {
  const escape = (value) => {
    const text = String(value ?? "");
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return [
    columns.map((c) => escape(c.label)).join(","),
    ...rows.map((row) => columns.map((c) => escape(c.value(row))).join(",")),
  ].join("\n");
};

export const downloadText = (filename, text, type = "text/csv") => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
